import { useState, useEffect, useCallback, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { getToken } from '../auth';
import { useTheme } from '../context/ThemeContext';
import { useSupportUrl } from '../hooks/useSupportUrl';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import { SkeletonCard } from '../components/Skeleton';
import { EmptyState, IconButton } from '../components/UI';
import Modal from '../components/Modal';
import Icon from '../components/Icon';

interface HomeContentItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  imageUrl: string | null;
  createdAt?: string;
}

interface UserProfile { fullName: string; }
interface WalletSummary { balance: number; }

const TYPE_ICON: Record<string, { icon: string }> = {
  promo: { icon: 'tag' },
  event: { icon: 'star' },
  announcement: { icon: 'bullhorn' },
  quote: { icon: 'quote-right' },
};

export default function Home() {
  const navigate = useNavigate();
  const { colors, isDark } = useTheme();
  const { alert } = useAlert();
  const toast = useToast();
  const supportUrl = useSupportUrl();

  const [content, setContent] = useState<HomeContentItem[]>([]);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [hideBalance, setHideBalance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAnnouncements, setShowAnnouncements] = useState(false);

  const [showPromo, setShowPromo] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [referral, setReferral] = useState<{ code: string; pendingCount: number; paidCount: number; totalEarned: number; rewardAmount: string } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const token = await getToken();
      const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      const [contentRes, userRes, walletRes] = await Promise.allSettled([
        axios.get<HomeContentItem[]>(`${API_BASE}/home-content`),
        token ? axios.get<UserProfile>(`${API_BASE}/auth/me`, auth) : Promise.reject(),
        token ? axios.get<WalletSummary>(`${API_BASE}/wallet/me`, auth) : Promise.reject(),
      ]);
      if (contentRes.status === 'fulfilled') setContent(contentRes.value.data);
      if (userRes.status === 'fulfilled') setUser(userRes.value.data);
      if (walletRes.status === 'fulfilled') setWallet(walletRes.value.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchReferral = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    try {
      const res = await axios.get(`${API_BASE}/me/referral`, { headers: { Authorization: `Bearer ${token}` } });
      setReferral(res.data);
    } catch { /* ignore */ }
  }, []);

  const submitPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoSubmitting(true);
    try {
      const token = await getToken();
      const res = await axios.post(
        `${API_BASE}/promo/redeem`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowPromo(false);
      setPromoInput('');
      alert('Promo redeemed', `$${res.data.amount} added to your wallet. New balance: $${res.data.newBalance.toFixed(2)}.`);
      fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Could not redeem code';
      alert('Promo failed', String(msg));
    } finally {
      setPromoSubmitting(false);
    }
  };

  const openReferral = async () => {
    setShowReferral(true);
    if (!referral) await fetchReferral();
  };

  const shareReferralCode = async () => {
    if (!referral?.code) return;
    const shareText = `Join AGA — All Game Arena and we both win! Use my referral code ${referral.code} when you sign up.`;
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ title: 'AGA Referral', text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success('Copied', 'Share text copied to clipboard');
      }
    } catch { /* ignored */ }
  };

  const balanceText = wallet ? `$ ${wallet.balance.toFixed(2)}` : '$ 0.00';
  const walletGradient = isDark
    ? 'linear-gradient(135deg, #05121f 0%, #073b3f 42%, #0f766e 72%, #14f1d1 100%)'
    : 'linear-gradient(135deg, #031f1d 0%, #07505a 42%, #0f9f95 72%, #34d399 100%)';
  const walletOverlay = 'linear-gradient(135deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.02) 50%, rgba(2,6,23,0.20) 100%)';
  const walletIconColor = isDark ? '#99f6e4' : '#67e8f9';

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg, position: 'relative' }}>
      <div style={{
        padding: 20, paddingBottom: 40,
        paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
      }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 24, backgroundColor: '#000',
                overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src="/icons/icon-512.png" alt="" style={{ width: 38, height: 38, objectFit: 'contain' }} />
              </div>
              <IconButton
                icon="bell"
                accessibilityLabel="Open notifications"
                onPress={() => navigate('/notifications')}
                backgroundColor={colors.card}
                color={colors.text}
              />
            </div>

            <div style={{ fontSize: 24, fontWeight: 800, color: colors.text, marginTop: 4 }}>
              Hello, {user ? user.fullName.split(' ')[0] : 'there'}
            </div>
            <div style={{ fontSize: 14, color: colors.textSub, marginTop: 2, marginBottom: 18 }}>
              Welcome back!
            </div>

            {/* Wallet Card */}
            <div
              style={{
                position: 'relative',
                display: 'flex', alignItems: 'center', gap: 8,
                borderRadius: 16, padding: 18, marginBottom: 22,
                background: walletGradient,
                border: `1px solid ${colors.cardBorder}`,
                overflow: 'hidden',
              }}
            >
              <div style={{
                position: 'absolute', inset: 0,
                background: walletOverlay, pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', top: -46, right: -28,
                width: 156, height: 156, borderRadius: '50%',
                backgroundColor: 'rgba(20,241,209,0.30)', pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', bottom: -70, left: -42,
                width: 148, height: 148, borderRadius: '50%',
                backgroundColor: 'rgba(56,189,248,0.18)', pointerEvents: 'none',
              }} />
              <div style={{ flex: 1, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={walletIconChip}>
                    <Icon name="wallet" size={13} color={walletIconColor} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.78)' }}>
                    Wallet Balance
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
                  <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 0.3, color: '#fff' }}>
                    {hideBalance ? '$ ••••' : balanceText}
                  </div>
                  <button
                    aria-label={hideBalance ? 'Show wallet balance' : 'Hide wallet balance'}
                    onClick={() => setHideBalance(v => !v)}
                    style={{
                      minWidth: 44, minHeight: 44, marginLeft: 2,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                    }}
                  >
                    <Icon name={hideBalance ? 'eye' : 'eye-slash'} size={14} color="rgba(255,255,255,0.68)" />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8 }}>
                  <div style={walletIconChipSm}>
                    <Icon name="shield-alt" size={11} color={walletIconColor} />
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.68)' }}>Secure & Encrypted</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 8, position: 'relative' }}>
                <button
                  onClick={() => navigate('/withdraw?mode=wallet')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 14px', borderRadius: 999,
                    backgroundColor: colors.secondaryRaised, color: '#fff',
                    border: 'none', cursor: 'pointer',
                    fontWeight: 900, fontSize: 12, minHeight: 44, minWidth: 112,
                  }}
                >
                  <Icon name="arrow-down" size={12} color="#fff" />
                  Withdraw
                </button>
                <button
                  onClick={() => navigate('/wallet-topup')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 14px', borderRadius: 999,
                    backgroundColor: colors.accent, color: '#020617',
                    border: 'none', cursor: 'pointer',
                    fontWeight: 800, fontSize: 12, minHeight: 44, minWidth: 112,
                  }}
                >
                  <Icon name="plus" size={12} color="#020617" />
                  Top-Up
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{ fontSize: 16, fontWeight: 800, color: colors.text, marginTop: 6, marginBottom: 12 }}>
              Quick Actions
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 6 }}>
              <QuickAction icon="wallet" title="Wallet Top-Up" subtitle="Add wallet balance" onClick={() => navigate('/wallet-topup')} />
              <QuickAction icon="arrow-up" title="Game Top-Up" subtitle="Load a game" onClick={() => navigate('/topup')} />
              <QuickAction icon="arrow-down" title="Withdraw" subtitle="Cash out" onClick={() => navigate('/withdraw')} />
              <QuickAction icon="headset" title="Support" subtitle="Get help" onClick={() => window.open(supportUrl, '_blank', 'noopener')} />
              <QuickAction icon="ticket-alt" title="Promo Code" subtitle="Redeem for credit" onClick={() => setShowPromo(true)} />
              <QuickAction icon="users" title="Refer & Earn" subtitle="Invite friends" onClick={openReferral} />
            </div>

            {/* Announcements */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 22, marginBottom: 12,
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>Announcements</div>
              {content.length > 0 && (
                <button
                  onClick={() => setShowAnnouncements(true)}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '0 4px', minHeight: 44,
                    fontSize: 13, fontWeight: 700, color: colors.accentDark,
                  }}
                >
                  View all
                </button>
              )}
            </div>

            {content.length === 0 ? (
              <EmptyState icon="bell-slash" title="Nothing to show yet" body="Promotions and announcements will appear here." />
            ) : (
              content.map(item => {
                const typeInfo = TYPE_ICON[item.type] || { icon: 'info-circle' };
                return (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex', gap: 12,
                      padding: 14, borderRadius: 14, marginBottom: 10,
                      backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: 12,
                      backgroundColor: colors.accentSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon name={typeInfo.icon} size={16} color={colors.accentText} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: 4, gap: 8,
                      }}>
                        <div style={{
                          flex: 1, fontSize: 14, fontWeight: 800, color: colors.text,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {item.title}
                        </div>
                        {item.createdAt && (
                          <div style={{ fontSize: 11, color: colors.textMuted, flexShrink: 0 }}>
                            {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                      {item.body && (
                        <div style={{
                          fontSize: 12, lineHeight: 1.45, color: colors.textSub,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {item.body}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {/* Floating Support FAB */}
      <button
        aria-label="Contact customer support"
        onClick={() => window.open(supportUrl, '_blank', 'noopener')}
        style={{
          position: 'fixed', right: 18,
          bottom: 'calc(68px + 24px + env(safe-area-inset-bottom, 0px))',
          width: 56, height: 56, borderRadius: 28,
          backgroundColor: colors.accentDark, color: '#020617',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 18px rgba(20,241,209,0.7)`,
          animation: 'aga-glow 2.4s ease-in-out infinite',
          zIndex: 90,
        }}
      >
        <Icon name="headset" size={20} color="#020617" />
      </button>

      <style>{`
        @keyframes aga-glow {
          0%, 100% { box-shadow: 0 0 4px rgba(20,241,209,0.35); }
          50%      { box-shadow: 0 0 18px rgba(20,241,209,0.95); }
        }
      `}</style>

      {/* Announcements Modal */}
      <Modal open={showAnnouncements} onClose={() => setShowAnnouncements(false)} card={false}>
        <div style={{
          backgroundColor: colors.bg, color: colors.text,
          minHeight: '100dvh', maxHeight: '100dvh', overflowY: 'auto',
          width: '100vw', position: 'fixed', inset: 0,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px',
            paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
            backgroundColor: colors.header, borderBottom: `1px solid ${colors.headerBorder}`,
            position: 'sticky', top: 0, zIndex: 10,
          }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: colors.text }}>Announcements</div>
            <button
              aria-label="Close"
              onClick={() => setShowAnnouncements(false)}
              style={{
                width: 44, height: 44, borderRadius: 22,
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon name="times" size={18} color={colors.textSub} />
            </button>
          </div>
          <div style={{ padding: '20px 20px 40px' }}>
            {content.map(item => {
              const typeInfo = TYPE_ICON[item.type] || { icon: 'info-circle' };
              return (
                <div
                  key={item.id}
                  style={{
                    borderRadius: 14, padding: 16, marginBottom: 12,
                    backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 12,
                      backgroundColor: colors.accentSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon name={typeInfo.icon} size={16} color={colors.accentText} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: 900, color: colors.text, marginBottom: 4 }}>
                        {item.title}
                      </div>
                      {item.createdAt && (
                        <div style={{ fontSize: 11, color: colors.textMuted }}>
                          {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt=""
                      style={{
                        width: '100%', height: 160, borderRadius: 12,
                        objectFit: 'cover', marginBottom: 12,
                        backgroundColor: '#e2e8f0',
                      }}
                    />
                  )}
                  {item.body && (
                    <div style={{ fontSize: 13, lineHeight: 1.55, color: colors.textSub, whiteSpace: 'pre-wrap' }}>
                      {item.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Promo Modal */}
      <Modal open={showPromo} onClose={() => setShowPromo(false)}>
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginBottom: 6 }}>Redeem Promo Code</div>
        <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
          Enter the code to add credit to your wallet.
        </div>
        <input
          placeholder="ENTER CODE"
          autoCapitalize="characters"
          autoCorrect="off"
          value={promoInput}
          onChange={e => setPromoInput(e.target.value.toUpperCase())}
          style={{
            width: '100%', padding: '12px 14px',
            border: `1px solid ${colors.cardBorder}`, borderRadius: 10,
            backgroundColor: 'transparent', color: colors.text,
            fontSize: 16, fontWeight: 700, letterSpacing: 2,
            textAlign: 'center', marginBottom: 14, outline: 'none',
          }}
        />
        <button
          onClick={submitPromo}
          disabled={promoSubmitting}
          style={{
            width: '100%', padding: '14px 0', borderRadius: 10,
            backgroundColor: colors.accentDark, color: '#020617',
            border: 'none', fontSize: 15, fontWeight: 800,
            cursor: promoSubmitting ? 'default' : 'pointer',
            opacity: promoSubmitting ? 0.6 : 1,
          }}
        >
          {promoSubmitting ? 'Redeeming…' : 'Redeem'}
        </button>
        <button
          onClick={() => setShowPromo(false)}
          style={{
            display: 'block', margin: '10px auto 0',
            background: 'transparent', border: 'none',
            color: colors.textMuted, cursor: 'pointer',
            padding: 10, minHeight: 44,
          }}
        >
          Cancel
        </button>
      </Modal>

      {/* Referral Modal */}
      <Modal open={showReferral} onClose={() => setShowReferral(false)}>
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginBottom: 6 }}>Refer & Earn</div>
        {referral ? (
          <>
            <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
              Share your code. You earn ${referral.rewardAmount} when a friend signs up and makes their first wallet top-up.
            </div>
            <div style={{
              border: `2px solid ${colors.accentDark}`,
              borderRadius: 12, padding: '18px 0',
              textAlign: 'center', marginBottom: 16,
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 4, color: colors.text }}>
                {referral.code}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16 }}>
              <RefStat num={String(referral.paidCount)} label="Paid" />
              <RefStat num={String(referral.pendingCount)} label="Pending" />
              <RefStat num={`$${referral.totalEarned.toFixed(2)}`} label="Earned" />
            </div>
            <button
              onClick={shareReferralCode}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 10,
                backgroundColor: colors.accentDark, color: '#020617',
                border: 'none', fontSize: 15, fontWeight: 800, cursor: 'pointer',
              }}
            >
              Share Code
            </button>
          </>
        ) : (
          <div style={{ fontSize: 13, color: colors.textMuted, padding: '12px 0' }}>Loading…</div>
        )}
        <button
          onClick={() => setShowReferral(false)}
          style={{
            display: 'block', margin: '10px auto 0',
            background: 'transparent', border: 'none',
            color: colors.textMuted, cursor: 'pointer',
            padding: 10, minHeight: 44,
          }}
        >
          Close
        </button>
      </Modal>
    </div>
  );
}

function QuickAction({ icon, title, subtitle, onClick }: { icon: string; title: string; subtitle: string; onClick: () => void }) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: 14, borderRadius: 14,
        backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
        cursor: 'pointer', textAlign: 'left', minHeight: 82,
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        backgroundColor: colors.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={icon} size={16} color={colors.accentText} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: colors.text }}>{title}</div>
        <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>{subtitle}</div>
      </div>
    </button>
  );
}

function RefStat({ num, label }: { num: string; label: string }) {
  const { colors } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{num}</div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}

const walletIconChip: CSSProperties = {
  width: 28, height: 28, borderRadius: 8,
  backgroundColor: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.26)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const walletIconChipSm: CSSProperties = {
  width: 22, height: 22, borderRadius: 6,
  backgroundColor: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.22)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
